import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom'; // Added useNavigate
import { setIsCollapsed } from '../redux/slices/commonSlice';
import { logout } from '../redux/slices/authSlice'; // Import your logout action
import { RootState } from '../redux/store';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import { FaUserCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';

const Header: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate(); // For programmatic redirection
    const dispatch = useDispatch();
    const { collapsed } = useSelector((state: RootState) => state.common);
    const { user } = useSelector((state: RootState) => state.auth); // Pull user data from Redux

    const [open, setOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const handler = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => {
            document.removeEventListener("mousedown", handler);
        };
    }, []);

    const toggleSidebar = () => {
        dispatch(setIsCollapsed(!collapsed));
    };

    // --- Logout Logic ---
    const handleLogout = () => {
        try {
            // 1. Clear Local Storage
            localStorage.removeItem("authToken");
            
            // 2. Dispatch Logout to Redux (Resets isAuthenticated and userData)
            dispatch(logout());
            
            // 3. Success notification
            toast.success("Logged out successfully");
            
            // 4. Redirect to login
            navigate('/login');
        } catch (error) {
            toast.error("Error during logout");
        }
    };

    return (
        <header className="border-b bg-light_primary border-borderColor text-cardBackground flex items-start p-2 shadow-md relative">
            <div className="w-full flex items-center justify-between">
                <div className="w-full flex items-center">
                    <button
                        onClick={toggleSidebar}
                        className="focus:outline-none text-primary p-1 rounded hover:bg-gray-800 transition"
                        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                        {collapsed ? <HiChevronRight size={30} /> : <HiChevronLeft size={30} />}
                    </button>
                    
                    <nav className="text-sm text-bodySecondary" aria-label="Breadcrumb">
                        <ol className="list-reset flex">
                            <li>
                                <Link to="/" className="hover:underline text-accent">Home</Link>
                            </li>
                            {location.pathname
                                .split('/')
                                .filter((x) => x)
                                .filter((name) => isNaN(Number(name)))
                                .map((name, index, arr) => {
                                    const routeTo = `/${arr.slice(0, index + 1).join('/')}`;
                                    const isLast = index === arr.length - 1;
                                    const displayName = name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' ');

                                    return (
                                        <li key={routeTo} className="flex items-center">
                                            <span className="mx-2 text-subTitleText">/</span>
                                            {isLast ? (
                                                <span className="text-subTitleText">{displayName}</span>
                                            ) : (
                                                <Link to={routeTo} className="hover:underline text-secondary">
                                                    {displayName}
                                                </Link>
                                            )}
                                        </li>
                                    );
                                })}
                        </ol>
                    </nav>
                </div>

                <div className="flex items-center justify-between mb-1">
                    <div className="relative" ref={menuRef}>
                        <button
                            onClick={() => setOpen(!open)}
                            className="flex items-center gap-2 p-2 rounded-full hover:bg-gray-100 transition"
                        >
                            <FaUserCircle size={28} className="text-primary" />
                        </button>

                        {open && (
                            <div className="absolute right-0 mt-2 w-52 bg-white shadow-xl rounded-lg border border-gray-100 z-50 py-2">
                                <div className="px-4 py-3 border-b border-gray-50">
                                    <p className="text-sm font-bold text-gray-800">
                                        {user?.name || "Admin User"}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                        {user?.email || "admin@iassets.com"}
                                    </p>
                                </div>

                                <div className="mt-1">
                                    <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition">
                                        Profile Settings
                                    </button>
                                    <button 
                                        onClick={handleLogout}
                                        className="w-full text-left px-4 py-2 text-sm text-red-600 font-semibold hover:bg-red-50 transition"
                                    >
                                        Logout
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;